// Pure helpers for payment-popup PURCHASE transaction label resolution.
// These functions are used by the view-transaction modal and the transaction list row,
// and are tested directly in purchase_labels.test.ts.
//
// Money-direction convention used consistently across this codebase (verified via
// isMoneyOutTransaction = CREDIT in transaction_item.tsx and arrow_up for CREDIT in the modal):
//   ETransactionType.CREDIT = buyer leg: money OUT of wallet (shown negative, includes fee)
//   ETransactionType.DEBIT  = merchant leg: money INTO wallet (shown positive)

import { EFundsOperation } from '@enum';
import { ETransactionAction, ETransactionType } from '@xeppt/xeppt-sdk/types';

/**
 * Returns the i18n key for the transaction-type label of a purchase operation,
 * or undefined if the operation is not a purchase.
 *
 * Uses string literals for the operation values so that the test is a genuine
 * fail-first regression: pre-fix, getInternalTransferAction returns undefined for
 * PAYMENT combinations, so the key is never 'purchase'.
 */
export function resolvePaymentNameKey(
    operation: EFundsOperation | undefined
): 'purchase' | undefined {
    if (
        operation === EFundsOperation.WALLET_CREDIT_PAYMENT ||
        operation === EFundsOperation.WALLET_DEBIT_PAYMENT
    ) {
        return 'purchase';
    }
    return undefined;
}

/**
 * Returns the i18n key for the contact subtitle shown below the counterparty name
 * in the transaction-detail modal, for PAYMENT transactions only.
 *
 * - Buyer leg (CREDIT / money out of wallet): 'online_store'  → buyer sees the merchant as "Online store"
 * - Merchant leg (DEBIT / money into wallet): 'xeppt_user'   → merchant sees the buyer as "XEPPT user"
 * - Non-PAYMENT: undefined (caller falls back to generic subtitle logic)
 */
export function resolvePaymentSubtitleKey(
    action: ETransactionAction,
    type: ETransactionType
): 'online_store' | 'xeppt_user' | undefined {
    if (action !== ETransactionAction.PAYMENT) return undefined;
    // CREDIT = buyer (money out) → show "Online store" for the merchant contact
    // DEBIT  = merchant (money in) → show "XEPPT user" for the buyer contact
    return type === ETransactionType.CREDIT ? 'online_store' : 'xeppt_user';
}
